import { forwardRef } from "react";
import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://indexrerum.com";
const DEFAULT_TITLE = "Index Rerum — Gestão de Património e Inventário Museológico Web";
const DEFAULT_DESCRIPTION =
  "Software web para gestão de património, inventário museológico e arquivos. CIDOC-CRM, multilingue, multi-site. Utilizado por mais de 250.000 registos.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const SEOHead = forwardRef<HTMLDivElement, SEOHeadProps>(
  ({ title, description, canonicalPath = "/", ogImage, noindex = false, jsonLd }, _ref) => {
    const fullTitle = title || DEFAULT_TITLE;
    const fullDescription = description || DEFAULT_DESCRIPTION;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const fullOgImage = ogImage || DEFAULT_OG_IMAGE;

    const jsonLdArray = jsonLd
      ? Array.isArray(jsonLd)
        ? jsonLd
        : [jsonLd]
      : [];

    return (
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={fullDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {noindex && <meta name="robots" content="noindex, nofollow" />}

        {/* Open Graph */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={fullDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={fullOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_PT" />
        <meta property="og:site_name" content="Index Rerum" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={fullDescription} />
        <meta name="twitter:image" content={fullOgImage} />

        {/* JSON-LD */}
        {jsonLdArray.map((ld, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(ld)}
          </script>
        ))}
      </Helmet>
    );
  }
);

SEOHead.displayName = "SEOHead";

export default SEOHead;
